/*
Copyright (c) 2025 Mathilde Meyer, Naziha Beghdadi
Licensed under the MIT License (see LICENSE.txt file for details)
*/

import React, { useState } from "react";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import TextField from "@mui/material/TextField";
import Collapse from "@mui/material/Collapse";
import { Box, Typography } from "@mui/material";

function Acclimatation({ index, data, setData }) {
  const [error, setError] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newData = [...data];
    newData[index][name] = value;
    setData(newData);

    // gestion de la condition (nb >= 7)
    if (name === "acclimatationDays") {
        const numericValue = parseInt(value, 10);
      if (isNaN(numericValue) || numericValue < 7) {
        setError(true);
      } else {
        setError(false);
      }
    }

    if (name === "acclimatation" && value === "no") {
      setError(false);
      delete newData[index].acclimatationDays;
      setData(newData);
    }
  };

  return (
    <div>
      <TextField
        id="experimentDuration"
        name="experimentDuration"
        label="How many days will this experiment last (from acclimatation, if any, until end)?"
        variant="outlined"
        fullWidth
        sx={{ mb: 2 }}
        value={data[index].experimentDuration || ""}
        onChange={handleChange}
        // impossible n'entrer autre chose que des chiffres
        onKeyDown={(e) => {
          const allowed = ["Backspace", "Tap", "ArrowLeft", "ArrowRight", "Delete"];
          if (!/[0-9]/.test(e.key) && !allowed.includes(e.key)) {
            e.preventDefault();
          }
        }}
        onPaste={(e) => {
          if (!/^\d+$/.test(e.clipboardData.getData("text"))) {
            e.preventDefault();
          }
        }}
        error={data[index]._formError === true}
        helperText={
          data[index]._formError
            ? "Declared duration is shorter than actual timeline."
            : "Total length of the experiment from day 0 to the last intervention day. Must be at least as long as your latest scheduled intervention."
        }
      />

      <FormControl fullWidth sx={{ mb: 1 }}>
        <FormLabel id="acclimatationLabel" sx={{ mb: 1, color: "text.primary", fontSize: "0.95rem" }}>
          Acclimatation period
        </FormLabel>
        <Box sx={{ mb: 1, fontSize: "0.84rem", color: "text.secondary", lineHeight: 1.6 }}>
          <Typography variant="body2">
            <strong>Imported animals</strong> (from an external supplier or another facility) must undergo a
            minimum of <strong>7 days</strong> of acclimatation before any intervention — this is a regulatory
            requirement to allow the animals to recover from transport stress and adapt to the new housing
            conditions. Select <em>Acclimatation</em> and enter the number of days.
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <strong>In-house bred animals</strong> (born and raised in your own facility) do not require a
            formal acclimatation period. Select <em>No Acclimatation</em>.
          </Typography>
        </Box>
        <RadioGroup
          aria-labelledby="acclimatationLabel"
          name="acclimatation"
          value={data[index].acclimatation || ""}
          onChange={handleChange}
        >
          <FormControlLabel value="yes" control={<Radio />} label="Acclimatation (imported animals)" />
          <FormControlLabel value="no" control={<Radio />} label="No acclimatation (in-house bred animals)" />
        </RadioGroup>
      </FormControl>

      <Collapse in={data[index].acclimatation === "yes"}>
        <TextField
          id="acclimatationDays"
          name="acclimatationDays"
          label="Duration of acclimatation (days)"
          variant="outlined"
          fullWidth
          sx={{ mb: 2, mt: 1 }}
          value={data[index].acclimatationDays || ""}
          onChange={handleChange}
          onKeyDown={(e) => {
            const allowed = ["Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete"];
            if (!/[0-9]/.test(e.key) && !allowed.includes(e.key)) e.preventDefault();
          }}
          onPaste={(e) => {
            if (!/^\d+$/.test(e.clipboardData.getData("text"))) e.preventDefault();
          }}
          error={error}
          helperText={
            error
              ? "Minimum 7 days required for imported animals (regulatory requirement)."
              : "Minimum 7 days. During acclimatation, animals are housed, fed normally, and monitored — no interventions are performed."
          }
        />
      </Collapse>
    </div>
  );
}

export default Acclimatation;
